import {
    CSSProperties,
    forwardRef,
    HTMLAttributes,
    ReactNode,
    Ref,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { getInlineOverflowState } from './inlineOverflow';

export interface OverflowFadeTextProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
    children: ReactNode;
    fadeSize?: CSSProperties['width'];
}

const assignRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
    if (typeof ref === 'function') {
        ref(value);
    } else if (ref) {
        const mutableRef = ref as { current: T | null };
        mutableRef.current = value;
    }
};

/**
 * Preserves single-line ancillary labels while adding an inline-end fade only
 * when the rendered content is genuinely clipped.
 */
const OverflowFadeText = forwardRef<HTMLSpanElement, OverflowFadeTextProps>(
    ({ children, fadeSize = '1.5rem', style, ...props }, forwardedRef) => {
        const elementRef = useRef<HTMLSpanElement | null>(null);
        const animationFrameRef = useRef<number | undefined>(undefined);
        const [overflowing, setOverflowing] = useState(false);
        const setElementRef = useCallback(
            (element: HTMLSpanElement | null) => {
                elementRef.current = element;
                assignRef(forwardedRef, element);
            },
            [forwardedRef],
        );
        const measure = useCallback(() => {
            const element = elementRef.current;
            const nextOverflowing = element ? getInlineOverflowState(element).overflowing : false;

            setOverflowing((current) => (current === nextOverflowing ? current : nextOverflowing));
        }, []);
        const scheduleMeasurement = useCallback(() => {
            if (typeof window === 'undefined') {
                return;
            }

            if (animationFrameRef.current !== undefined) {
                window.cancelAnimationFrame(animationFrameRef.current);
            }

            animationFrameRef.current = window.requestAnimationFrame(() => {
                animationFrameRef.current = undefined;
                measure();
            });
        }, [measure]);

        useLayoutEffect(() => {
            const element = elementRef.current;

            if (!element) {
                return;
            }

            scheduleMeasurement();

            const resizeObserver =
                typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(scheduleMeasurement);
            resizeObserver?.observe(element);

            const mutationObserver =
                typeof MutationObserver === 'undefined' ? undefined : new MutationObserver(scheduleMeasurement);
            mutationObserver?.observe(element, { characterData: true, childList: true, subtree: true });
            window.addEventListener('resize', scheduleMeasurement);

            return () => {
                resizeObserver?.disconnect();
                mutationObserver?.disconnect();
                window.removeEventListener('resize', scheduleMeasurement);

                if (animationFrameRef.current !== undefined) {
                    window.cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = undefined;
                }
            };
        }, [scheduleMeasurement]);

        const resolvedFadeSize = typeof fadeSize === 'number' ? `${fadeSize}px` : fadeSize;
        const mask = overflowing
            ? `linear-gradient(90deg, #000 0%, #000 calc(100% - ${resolvedFadeSize}), transparent 100%)`
            : undefined;

        return (
            <span
                {...props}
                data-inline-overflow={overflowing ? 'true' : 'false'}
                ref={setElementRef}
                style={{
                    display: 'block',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    WebkitMaskImage: mask,
                    maskImage: mask,
                    ...style,
                }}
            >
                {children}
            </span>
        );
    },
);

OverflowFadeText.displayName = 'OverflowFadeText';

export default OverflowFadeText;
